// Current-board persistence for the sortaballs family. A single localStorage
// key holds the in-progress board of the most recently played puzzle, keyed by
// the puzzle's content-address `boardId`, together with the committed move
// list — so a reload (or navigating back to a puzzle) can REPLAY the moves and
// rebuild the session's undo/reset history instead of restoring a bare board.
// On mount we restore it only when the puzzle the player is on matches the
// saved id. Challenge/battle boards are never saved or restored, and a win
// clears the stored board so replaying that puzzle starts fresh.
import type { CommittedMove, GameState } from "../sortaballs-8-colors/logic"

const CURRENT_PUZZLE_KEY = "current.sortaballs.puzzle"

export interface RestoredCurrentPuzzle {
  boardState: GameState
  moves: CommittedMove[]
}

interface StoredCurrentPuzzle {
  boardState: GameState
  id: string
  moves: CommittedMove[]
}

// Clears the stored in-progress board (called on a win or a reset).
export function clearCurrentPuzzle(): void {
  try {
    localStorage.removeItem(CURRENT_PUZZLE_KEY)
  } catch {
    // localStorage unavailable
  }
}

// Returns the saved board + moves for `boardId`, or null when there's no match
// (or the stored payload is malformed). Legacy payloads without moves restore
// as an empty history.
export function loadCurrentPuzzleBoard(
  boardId: string,
): null | RestoredCurrentPuzzle {
  try {
    const raw = localStorage.getItem(CURRENT_PUZZLE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<StoredCurrentPuzzle>
    if (stored.id === boardId && Array.isArray(stored.boardState)) {
      return {
        boardState: stored.boardState as GameState,
        moves: stored.moves ?? [],
      }
    }
  } catch {
    return null
  }
  return null
}

// Overwrites the stored in-progress board + moves for `boardId`. Single key:
// the most recently touched puzzle wins, and its id disambiguates which one it
// came from.
export function saveCurrentPuzzleBoard(
  boardId: string,
  boardState: GameState,
  moves: CommittedMove[],
): void {
  try {
    localStorage.setItem(
      CURRENT_PUZZLE_KEY,
      JSON.stringify({ boardState, id: boardId, moves }),
    )
  } catch {
    // localStorage unavailable
  }
}

// Clears the stored session only when it belongs to `boardId`. Used when the
// current board has no committed moves: a stale same-board session must not
// resurrect on reload, while another puzzle's session stays untouched (the
// single key holds the most recent board).
export function clearCurrentPuzzleIfMatches(boardId: string): void {
  if (loadCurrentPuzzleBoard(boardId) !== null) clearCurrentPuzzle()
}
